import React from 'react'
import { useFormikContext } from 'formik'
import { BasicTextFieldProps } from '../BasicTextField'
import { UploadedFile } from '../../UploadedFile'
import { FileUploadInput } from '../../FileUploadInput'
import { FormControl, FormHelperText, Typography } from '@mui/material'
import tw from 'twin.macro'
import { RiskMinor } from '@shopify/polaris-icons'

const ACCESS_TYPES = ['image/jpeg', 'image/png', 'application/pdf']

const deriveRemovePrefixFromInputTestId = (inputDataTestId?: string) =>
  inputDataTestId?.endsWith('-input') ? inputDataTestId.replace(/-input$/, '') : undefined

export const FormikMultipleFileInput = <T extends unknown>({
  name,
  label,
  inputDataTestId,
  uploadedFileRemoveDataTestIdPrefix,
  hideAfterUploadHint,
}: BasicTextFieldProps<T> & {
  inputDataTestId?: string
  uploadedFileRemoveDataTestIdPrefix?: string
  hideAfterUploadHint?: boolean
}) => {
  const removeIdPrefix =
    uploadedFileRemoveDataTestIdPrefix ?? deriveRemovePrefixFromInputTestId(inputDataTestId)
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<T>()

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined
  const files = (values[name] as File[] | null | undefined) || []

  const handleAddFile = (file: File) => {
    const currentFiles = (values[name] as File[] | null | undefined) || []
    setFieldValue(name as string, [...currentFiles, file])
  }

  const handleRemoveFile = (index: number) => {
    const currentFiles = (values[name] as File[] | null | undefined) || []
    const newFiles = currentFiles.filter((_, i) => i !== index)
    setFieldValue(name as string, newFiles.length > 0 ? newFiles : null)
    setFieldTouched(name as string, true)
  }

  return (
    <div>
      <FormControl fullWidth error={!!error} css={tw`p-0`}>
        {label && (
          <Typography
            css={tw`mb-2 normal-case`}
            sx={{ fontSize: '14px', color: '#847E96', fontWeight: 600 }}
            variant="overline"
          >
            {label}
          </Typography>
        )}
        {files.length > 0 && (
          <div css={tw`mb-0`}>
            {files.map((file, index) => (
              <div key={`${file.name}-${index}`} css={tw`[&>div]:mb-4`}>
                <UploadedFile
                  file={file}
                  onRemove={() => handleRemoveFile(index)}
                  removeButtonTestId={
                    removeIdPrefix ? `${removeIdPrefix}-${index}-remove-btn` : undefined
                  }
                />
              </div>
            ))}
          </div>
        )}
        <div css={tw`mt-0`}>
          <FileUploadInput
            fileSizeLimit={10000000}
            acceptTypes={ACCESS_TYPES}
            onSelect={handleAddFile}
            inputDataTestId={inputDataTestId}
          />
        </div>
        {files.length > 0 && !hideAfterUploadHint && (
          <Typography css={tw`mt-4 text-shades-400 text-center`} variant="body1">
            After you upload a file, you'll be able to upload another one
          </Typography>
        )}
        {isTouched && error ? (
          <FormHelperText component="div" css={tw`ml-0`}>
            <div css={tw`flex items-center gap-1`}>
              <RiskMinor width={20} css={tw`fill-critical-500`} />
              <Typography variant="body2" css={tw`text-critical-500`} component="span">
                {error}
              </Typography>
            </div>
          </FormHelperText>
        ) : null}
      </FormControl>
    </div>
  )
}
